/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Thursday, 5th September 2019
 *
 * Mobx Location Store
 */
import { configure, observable, computed, action, decorate, reaction } from 'mobx';
import { firebase } from 'containers/services/Firebase';
configure({ enforceActions: 'observed' });

class LocationStore {
  location = {};
  locationUsers = [];
  userInvites = [];
  badgeHistory = observable([]);
  hasChanges = false;
  isLoading = false;
  isUserLoading = false;
  isUserInvitationsLoading = false;
  isBadgeHistoryLoading = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.setField = this.setField.bind(this);

    /**
     * Location User Listener
     */

    reaction(
      () => this.stores.authStore.isLocationUser,
      () => {
        // Get default location
        if (this.stores.authStore.claims.hasOwnProperty('location')) {
          //const location = this.stores.authStore.claims.location[0];
          //this.setLocation(Object.keys(location)[0])
        }
      }
    );
  }

  /**
   * Internal stores getter.  Note: NOT mobx computed
   */
  get stores() {
    return this.rootStore;
  }

  /**
   * The id of the current store location
   * Mobx: computed
   * @returns {string} Id of current store location.
   */
  get locationId() {
    return this.location.id;
  }

  /**
   * Checks to see if current user is a location admin
   * Mobx: computed
   * @returns {boolean}
   */
  get isAdmin() {
    if (this.stores.authStore.isAuth) {
      if (
        this.stores.authStore.claims.isAdmin ||
        (this.stores.authStore.hasCompanyRole(this.stores.companyStore.companyId, 'Admin') ||
          this.stores.authStore.hasLocationRole(this.locationId, 'Admin'))
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks to see if userId is included in this.location.users array.
   * @param {string} uid User identification number to check.
   * @returns {boolean}
   */
  userExists(uid) {
    let exists = false;
    this.locationUsers.forEach(user => {
      if (user.uid === uid) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Checks to see if invite id is included in this.userInvites array.
   * @param {string} id Invite identification number to check.
   * @returns {boolean}
   */
  inviteExists(id) {
    let exists = false;
    this.userInvites.forEach(invite => {
      if (invite.id === id) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Checks to see if invite id is included in this.userInvites array.
   * @param {string} email Invite identification number to check.
   * @returns {boolean}
   */
  inviteExistsByEmail(email) {
    let exists = false;
    this.userInvites.forEach(invite => {
      if (invite.email === email) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Generic action to set value of any store field.
   * @param {string} fieldName Name of store field to set.
   * @param {any} value Value of store field to set.
   * @returns {void}
   */
  setField(fieldName, value) {
    if (this[fieldName] !== undefined) {
      this[fieldName] = value;
    }
  }

  /**
   * Generic action to replace store array index value.
   * @param {array} array Store array to update
   * @param {int} index Array index to replace.
   * @param {any} newValue New array value.
   * @returns {void}
   */
  setArrayField(array, index, newValue) {
    array[index] = newValue;
  }

  /**
   * Handles the location profile form office name field updates
   * @param {Event} e HTML form event object.
   * @returns {void}
   */
  handleNameChange = e => {
    this.setField('hasChanges', true);
    this.location.name = e.target.value;
  };

  /**
   * Handles the location profile form address field updates
   * @param {Event} e HTML form event object.
   * @returns {void}
   */
  handleAddressChange = e => {
    this.setField('hasChanges', true);
    this.location.address[e.target.id] = e.target.value;
  };

  /**
   * Removed user invitation from the database
   * @function handleRemoveInvite
   * @param {Event} event HTML onClick event
   * @param {Object} rowData User invite.
   */
  handleRemoveInvite = (event, rowData) => {
    this.setField('isUserInvitationsLoading', true);
    const index = this.userInvites.indexOf(rowData);
    // Check for index length
    if (this.userInvites.length > 1) {
      const newValues = this.userInvites.splice(index, 1);
      this.setField('userInvites', newValues);
    } else {
      this.setField('userInvites', []);
    }

    // Remove Database Record
    firebase.data
      .ref('userInvites')
      .child(rowData.id)
      .remove()
      .then(() => {
        this.rootStore.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'User invite removed successfully.',
          'success',
          2750
        );
        this.setField('isUserInvitationsLoading', false);
      })
      .catch(error => {
        this.setField('isUserInvitationsLoading', false);
        this.rootStore.appStore.error('system', error);
      });
  };

  /**
   * Resends user invitation email
   * @function handleSendInvite
   * @param {Event} event HTML onClick event
   * @param {Object} rowData User invite.
   */
  handleSendInvite = (event, rowData) => {
    this.setField('isUserInvitationsLoading', true);
    firebase
      .sendInviteEmail({ invite: rowData })
      .then(() => {
        this.rootStore.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'Email sent successfully.',
          'success',
          2750
        );
        this.setField('isUserInvitationsLoading', false);
      })
      .catch(error => {
        this.setField('isUserInvitationsLoading', false);
        this.rootStore.appStore.error('system', error);
      });
  };

  /**
   * Handles the location profile form onSubmit event
   * @param {Event} e HTML form onSubmit event object.
   * @returns {void}
   */
  onSubmit = e => {
    e.preventDefault();
    firebase.data
      .ref('location')
      .child(this.location.id)
      .update(this.location)
      .then(() => {
        // Success
        this.setField('hasChanges', false);
        this.rootStore.portalStore.snackbar.showSnackbarWithVariantWithDuration(
          'Profile updates saved successfully.',
          'success',
          2750
        );
      })
      .catch(error => {
        this.rootStore.appStore.error('system', error);
      });
  };

  /**
   * Sets the current store location
   * @function setLocation
   * @action Mobx Action
   * @param {string} locationId The identification number of the location to set.
   * @returns {void}
   */
  setLocation = locationId => {
    if (locationId) {
      this.setField('isLoading', true);
      if (locationId && this.location.id !== locationId) {
        this.rootStore.appStore.debug('Location.setLocation [action] Fetching location...');
        firebase
          .getLocationById(locationId)
          .then(location => {
            // Success
            this.setField('location', location);
            this.setField('isLoading', false);
            this.rootStore.appStore.debug('Location.setLocation [action] New location selected', location);
          })
          .then(() => {
            this.setLocationUsers();
            this.getUserInvites();
            this.fetchBadgeHistory(locationId);
          })
          .catch(error => {
            // Error
            this.setField('isLoading', false);
            this.rootStore.appStore.error('system', error);
          });
      }
    } else {
      // Not a valid location id
    }
  };

  /**
   * Sets the locationUsers store value
   * @function setLocationUsers
   * @returns {void}
   */
  setLocationUsers = () => {
    this.setField('isUserLoading', true);
    this.rootStore.appStore.debug('Location.setLocationUsers [action] Fetching location users...');
    this.getUsers(this.location)
      .then(users => {
        this.setField('locationUsers', users);
        this.setField('isUserLoading', false);
        this.rootStore.appStore.debug('Location.getLocationUsers [action] Users retrieved.', users);
      })
      .catch(error => {
        this.setField('isUserLoading', false);
        this.rootStore.appStore.error('system', error);
      });
  };

  /**
   * Fetches location users and their attributes from database
   * @param {object} location Location database object
   * @returns {void}
   */
  getUsers = async location => {
    if (location.users) {
      const users = Object.keys(location.users).map(id => {
        return firebase.fetchUserData(id).then(user => {
          user.role = location.users[user.uid].role || '';
          user.active = location.users[user.uid].active || '';
          return user;
        });
      });
      // Returns once all promises are resolved
      return Promise.all(users);
    } else {
      // No users to fetch
      return [];
    }
  };

  /**
   * Fetches user invitationsfrom database
   * @function getUserInvites
   * @returns {void}
   */
  getUserInvites = () => {
    this.setField('isUserInvitesLoading', true);
    this.setField('userInvites', []);
    // Loop through invitations and only show those relating to this location
    firebase.data.ref('userInvites').once(
      'value',
      snapshot => {
        if (snapshot.exists()) {
          // Find this location's invitations
          let locationInvites = [];
          snapshot.forEach(snap => {
            const invite = snap.val();
            if (invite.locationId === this.location.id && !this.inviteExists(invite.id)) {
              locationInvites.push(invite);
            }
          });
          this.setField('userInvites', locationInvites);
          this.setField('isUserInvitesLoading', false);
          this.rootStore.appStore.debug('Location.getUserInvitations [action] User invitations retrieved.');
        }
      },
      error => {
        this.setField('isUserInvitesLoading', false);
        this.rootStore.appStore.error('system', error);
      }
    );
  };

  /**
   * Update location user claims
   */
  updateUser = (newData, oldData) => {
    return new Promise((resolve, reject) => {
      const index = this.locationUsers.indexOf(oldData);

      firebase
        .updateLocationUser({
          companyId: this.location.companyId,
          locationId: this.location.id,
          newData: newData,
        })
        .then(() => {
          // Success
          this.setArrayField(this.locationUsers, index, newData);
          this.rootStore.appStore.debug('Location.setUser [action] Database updated successfully', newData.role);
          this.rootStore.portalStore.snackbar.showSnackbarWithVariantWithDuration(
            'User updated successfully.',
            'success',
            2750
          );
          resolve();
        })
        .catch(error => {
          this.rootStore.appStore.error('system', error);
          reject();
        });
    });
  };

  /**
   * Fetches the badge history for location.
   * @function fetchBadgeHistory
   * @returns {void}
   */
  fetchBadgeHistory = async locationId => {
    this.setField('isBadgeHistoryLoading', true);
    this.stores.appStore.debug('LocationStore.fetchBadgeHistory() [action] Fetching badge history...');
    firebase
      .getLocationCheckins(locationId)
      .then(badgeHistory => {
        this.setField('badgeHistory', badgeHistory);
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.debug('LocationStore.fetchBadgeHistory() [action] Badge history retrieved.', badgeHistory);
      })
      .catch(error => {
        this.setField('isBadgeHistoryLoading', false);
        this.stores.appStore.error('system', error);
      });
  };

  /**
   * Resets store values to default
   */
  reset = () => {
    this.location = {};
    this.locationUsers = [];
    this.userInvites = [];
    this.hasChanges = false;
    this.isLoading = false;
    this.isUserLoading = false;
    this.isUserInvitesLoading = false;
    this.isBadgeHistoryLoading = false;
    this.stores.appStore.debug('Location.reset() [action] Location store has been reset.');
  };
}
decorate(LocationStore, {
  location: observable,
  locationUsers: observable,
  userInvites: observable,
  hasChanges: observable,
  isLoading: observable,
  isUserLoading: observable,
  isUserInvitesLoading: observable,
  isBadgeHistoryLoading: observable,
  handleNameChange: action,
  handleAddressChange: action,
  handleRemoveInvite: action,
  handleSendInvite: action,
  handleSaveChanges: action,
  locationId: computed,
  isAdmin: computed,
  locationExists: action,
  userExists: action,
  inviteExists: action,
  inviteExistsByEmail: action,
  setField: action,
  setArrayField: action,
  setLocation: action,
  setLocationUsers: action,
  getLocationUsers: action,
  getUserInvites: action,
  setUser: action,
  getBadgeHistory: action,
  reset: action,
});

export default LocationStore;
